import React, { useState, useEffect, useCallback } from 'react';
import {
  getMemberAllRegistrations,
  getEvent,
  getEventMedia,
  submitMemberPhoto,
  fmtDateTime,
} from '../../../api/eventsApi';

// ── Visibility badge ──────────────────────────────────────────────────────────
function visibilityBadge(visibility) {
  const map = {
    CONFIRMED_ONLY: { cls: 'bb-member-badge--green',  label: 'Confirmed only' },
    ALL_MEMBERS:    { cls: 'bb-member-badge--blue',   label: 'All members' },
    ADMIN_ONLY:     { cls: 'bb-member-badge--amber',  label: 'Admin only' },
  };
  const { cls, label } = map[visibility] || { cls: 'bb-member-badge--grey', label: visibility || '—' };
  return <span className={`bb-member-badge ${cls}`} style={{ fontSize: 11 }}>{label}</span>;
}

// ── Main component ────────────────────────────────────────────────────────────
function EventResourcesMember({ orgId, memberId, memberName }) {
  // Eligible events (CONFIRMED or CHECKED_IN)
  const [eligibleEvents, setEligibleEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEvent, setSelectedEvent]     = useState(null);

  // Media
  const [materials, setMaterials]   = useState([]);
  const [photos, setPhotos]         = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError]     = useState('');

  // Photo upload
  const [photoFile, setPhotoFile]     = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [uploadMsg, setUploadMsg]     = useState('');

  // Initial load
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // ── Load eligible events ──────────────────────────────────────────────────
  const loadEligible = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const regs = await getMemberAllRegistrations(orgId, memberId);
      const eligible = regs.filter(
        (r) => r.status === 'CONFIRMED' || r.status === 'CHECKED_IN'
      );

      const withEvents = await Promise.all(
        eligible.map(async (reg) => {
          try {
            const ev = await getEvent(orgId, reg.event_id);
            return { registration: reg, event: ev };
          } catch (_) {
            return { registration: reg, event: null };
          }
        })
      );

      // De-duplicate by event_id
      const seen = new Set();
      const unique = withEvents.filter(({ event }) => {
        if (!event || seen.has(event.id)) return false;
        seen.add(event.id);
        return true;
      });

      setEligibleEvents(unique);
    } catch (err) {
      setError(err.message || 'Failed to load your events.');
    } finally {
      setLoading(false);
    }
  }, [orgId, memberId]);

  useEffect(() => { loadEligible(); }, [loadEligible]);

  // ── Load media when event selected ────────────────────────────────────────
  useEffect(() => {
    if (!selectedEventId) {
      setMaterials([]);
      setPhotos([]);
      setSelectedEvent(null);
      return;
    }

    const found = eligibleEvents.find(({ event }) => event?.id === selectedEventId);
    setSelectedEvent(found?.event || null);

    const loadMedia = async () => {
      setMediaLoading(true);
      setMediaError('');
      setMaterials([]);
      setPhotos([]);
      try {
        const [mats, phts] = await Promise.all([
          getEventMedia(orgId, selectedEventId, 'PRE_EVENT_MATERIAL'),
          getEventMedia(orgId, selectedEventId, 'POST_EVENT_PHOTO'),
        ]);
        setMaterials(mats);
        setPhotos(phts);
      } catch (err) {
        setMediaError(err.message || 'Failed to load event resources.');
      } finally {
        setMediaLoading(false);
      }
    };
    loadMedia();
  }, [selectedEventId, orgId, eligibleEvents]);

  // ── Photo upload ──────────────────────────────────────────────────────────
  const handlePhotoUpload = async () => {
    if (!photoFile || !selectedEventId) return;
    setUploading(true);
    setUploadMsg('');
    try {
      await submitMemberPhoto(orgId, selectedEventId, photoFile, memberId, memberName);
      setUploadMsg('Photo submitted for review!');
      setPhotoFile(null);
      // Reset file input
      const input = document.getElementById('bb-photo-upload-input');
      if (input) input.value = '';
    } catch (err) {
      setUploadMsg(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div className="bb-loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="bb-member-section-title">Event Resources</h2>
      <p className="bb-member-section-sub">Access materials and photos for events you attended or are registered for.</p>

      {error && <div className="bb-alert bb-alert--error">{error}</div>}

      {/* Event selector */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Select an event
        </label>
        {eligibleEvents.length === 0 ? (
          <div className="bb-empty-state" style={{ padding: '16px 0', color: '#64748B', fontSize: 14 }}>
            You have no confirmed or attended events yet.
          </div>
        ) : (
          <select
            className="bb-select"
            value={selectedEventId}
            onChange={(e) => {
              setSelectedEventId(e.target.value);
              setUploadMsg('');
              setPhotoFile(null);
            }}
            style={{ padding: '8px 12px', fontSize: 13, minWidth: 300 }}
          >
            <option value="">— Choose an event —</option>
            {eligibleEvents.map(({ event }) => (
              <option key={event.id} value={event.id}>
                {event.title}
                {event.start_datetime ? ` — ${fmtDateTime(event.start_datetime)}` : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Content for selected event */}
      {selectedEventId && (
        <>
          {mediaLoading && (
            <div style={{ textAlign: 'center', padding: 30 }}>
              <div className="bb-loading-spinner" />
            </div>
          )}

          {!mediaLoading && mediaError && (
            <div className="bb-alert bb-alert--error">{mediaError}</div>
          )}

          {!mediaLoading && !mediaError && (
            <>
              {/* Pre-event materials */}
              <section style={{ marginBottom: 28 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', marginBottom: 12 }}>
                  Pre-Event Materials
                </h3>
                {materials.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#94A3B8', padding: '10px 0' }}>
                    No materials available for this event yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {materials.map((item) => (
                      <div
                        key={item.id}
                        className="bb-member-card"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 20 }}>📄</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
                              {item.file_name || 'Untitled file'}
                            </div>
                            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                              {item.version_note && `Version note: ${item.version_note}`}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {visibilityBadge(item.visibility)}
                          {item.file_url && (
                            <a
                              href={item.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: 12,
                                color: '#0D9488',
                                textDecoration: 'none',
                                border: '1px solid #0D9488',
                                borderRadius: 5,
                                padding: '3px 10px',
                              }}
                            >
                              Download / View
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Post-event photos */}
              <section style={{ marginBottom: 28 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', marginBottom: 12 }}>
                  Event Photos
                </h3>
                {photos.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#94A3B8', padding: '10px 0' }}>
                    No photos available for this event yet.
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {photos.map((item) => (
                      <div
                        key={item.id}
                        style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid #E2E8F0' }}
                      >
                        <img
                          src={item.file_url}
                          alt={item.file_name || 'Event photo'}
                          style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }}
                        />
                        {item.uploaded_by_name && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: 'rgba(0,0,0,0.45)',
                              color: '#fff',
                              fontSize: 11,
                              padding: '3px 6px',
                            }}
                          >
                            {item.uploaded_by_name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Share Your Photos (if consent required on event) */}
              {selectedEvent?.photo_consent_required && (
                <section>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', marginBottom: 6 }}>
                    Share Your Photos
                  </h3>
                  <p style={{ fontSize: 13, color: '#64748B', marginBottom: 12 }}>
                    Share a photo from this event. It will be reviewed before being published.
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    <input
                      id="bb-photo-upload-input"
                      type="file"
                      accept="image/*"
                      className="bb-input"
                      style={{ fontSize: 13 }}
                      onChange={(e) => {
                        setPhotoFile(e.target.files?.[0] || null);
                        setUploadMsg('');
                      }}
                    />
                    <button
                      className="bb-btn-primary"
                      style={{
                        background: photoFile && !uploading ? '#0D9488' : '#94A3B8',
                        border: 'none',
                        color: '#fff',
                        padding: '8px 18px',
                        borderRadius: 6,
                        fontSize: 13,
                        cursor: photoFile && !uploading ? 'pointer' : 'not-allowed',
                      }}
                      disabled={!photoFile || uploading}
                      onClick={handlePhotoUpload}
                    >
                      {uploading ? 'Uploading...' : 'Submit Photo'}
                    </button>
                  </div>

                  {uploadMsg && (
                    <div
                      style={{
                        marginTop: 12,
                        fontSize: 13,
                        padding: '8px 14px',
                        borderRadius: 6,
                        background: uploadMsg.includes('review') ? '#D1FAE5' : '#FEE2E2',
                        color: uploadMsg.includes('review') ? '#065F46' : '#991B1B',
                      }}
                    >
                      {uploadMsg}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default EventResourcesMember;
